import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import API, { UPLOADS_URL } from '../api/axios';

const FALLBACK_ACTIVITIES = [
  { id: 1, title: 'Football', category: 'Sports', description: 'Standard lush green football turf fostering teamwork, strategic play, agility, and physical stamina under certified football coaches.', icon: 'fa-futbol', image: 'activities/Football.jpg' },
  { id: 2, title: 'Taekwondo & Martial Arts', category: 'Martial Arts', description: 'Professional martial arts training emphasizing discipline, mental focus, rapid reflexes, and essential self-defense techniques.', icon: 'fa-hand-fist', image: 'activities/Taekwondo.jpg' },
  { id: 3, title: 'Skating', category: 'Sports', description: 'Smooth, dedicated outdoor skating rink guiding students from basic balance to advanced roller skating and speed techniques safely.', icon: 'fa-person-skating', image: 'activities/Skating.jpg' },
  { id: 4, title: 'Swimming Pool', category: 'Aquatics', description: 'Hygienic, temperature-regulated swimming pool with continuous water purification and certified coaches catering to beginners and competitive swimmers.', icon: 'fa-person-swimming', image: 'activities/Swimming-Pool.jpg' },
  { id: 5, title: 'Chess Club', category: 'Mind Games & Strategy', description: 'Intellectual strategy club sharpening critical reasoning, foresight, problem-solving, and patience through regular tournaments.', icon: 'fa-chess', image: 'activities/Chess.jpg' },
  { id: 6, title: 'Cricket Academy', category: 'Sports', description: 'Full-size cricket ground with professional turf and net practice pitches designed to cultivate bowling, batting, and fielding excellence.', icon: 'fa-baseball-bat-ball', image: 'activities/Cricket.jpg' },
  { id: 7, title: 'Badminton', category: 'Sports', description: 'Indoor non-slip badminton courts enabling fast-paced rallies, hand-eye coordination, and tournament training.', icon: 'fa-table-tennis-paddle-ball', image: 'activities/Badminton.jpg' },
  { id: 8, title: 'Basketball', category: 'Sports', description: 'Official regulation basketball court cultivating speed, dribbling finesse, tactical team coordination, and athletic leadership.', icon: 'fa-basketball', image: 'activities/Basketball.jpg' },
  { id: 9, title: 'Visual Arts & Craft', category: 'Creative Arts', description: 'Imaginative studio exploring sketching, canvas painting, pottery, origami, and annual student art exhibitions.', icon: 'fa-palette', image: 'activities/Art-Craft.jpg' },
  { id: 10, title: 'Dance & Theatre', category: 'Performing Arts', description: 'Vibrant studio for classical Indian dance, contemporary western styles, theatrical drama, and expressive stage performance.', icon: 'fa-masks-theater', image: 'activities/Dance.jpg' },
  { id: 11, title: 'Music & Vocal Studio', category: 'Performing Arts', description: 'Acoustically soundproofed studio offering hands-on training in synthesizer, guitar, drums, tabla, and classical/western vocal techniques.', icon: 'fa-music', image: 'activities/Music.jpg' },
  { id: 12, title: 'Table Tennis', category: 'Indoor Sports', description: 'High-standard indoor table tennis arena promoting quick reflexes, focused precision, and inter-house competitive spirit.', icon: 'fa-table-tennis-paddle-ball', image: 'activities/Table-Tennis.jpg' },
];

const resolveImage = (src, fallback = '') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const stripHtml = (html) => {
  if (!html) return '';
  return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const renderParagraphs = (raw, fallback = '') => {
  const text = raw || fallback;
  const parts = String(text).replace(/\r/g, '').split(/\n\s*\n|\n/).map(s => s.trim()).filter(Boolean);
  return parts.map((p, i) => <p key={i} className="mb-4 text-muted leading-relaxed text-[15px]">{p}</p>);
};

const ExtraCurriculumActivitiesPage = () => {
  const pageData = usePage('extra-curriculum-activities');
  const sections = pageData.sections || {};

  const banner = sections.banner || pageData.banner || {};
  const bannerTitle = banner.title || pageData.title || "Extracurricular Activities";
  const bannerSubtitle = banner.subtitle || "Fostering Talents, Leadership and Holistic Growth Beyond the Classroom";
  const bannerBreadcrumb = banner.breadcrumb || "ACTIVITIES";
  const bannerImgSrc = pageData.bannerImage || (pageData.banner_image ? resolveImage(pageData.banner_image) : null) || '/images/page-banners/extra-curricullam.jpg';

  const intro = sections.intro || "At St. Andrews World School, Sonipat, we strive to offer an educational process that promotes togetherness and creates a stress-free learning experience. Our extra-curricular activities are focused on motivating students to compete and push beyond their boundaries. We value individuality and know that every student has different talents and demands special attention.";

  const [activities, setActivities] = useState([]);

  useEffect(() => {
    API.get('/activities/public')
      .then((res) => {
        if (res.data?.data && Array.isArray(res.data.data) && res.data.data.length > 0) {
          setActivities(res.data.data);
        }
      })
      .catch((err) => {
        console.error('Failed to load activities list for extracurricular page:', err);
      });
  }, []);

  const displayedActivities = activities.length > 0 ? activities : FALLBACK_ACTIVITIES;

  return (
    <>
      <SEO
        title={pageData.title || "Extracurricular Activities"}
        description="Explore clubs, sports, visual arts, and performing arts at St. Andrews World School, Sonipat."
        keywords="Extracurricular Activities, Sports, Arts, Drama, Clubs, SAWS Sonipat"
      />

      <PageBanner
        title={bannerTitle}
        subtitle={bannerSubtitle}
        breadcrumb={bannerBreadcrumb}
        bgImage={bannerImgSrc}
        fallback="/images/page-banners/extra-curricullam.jpg"
      />

      {/* ACTIVITIES & SPORTS GRID */}
      <section className="section bg-surface-alt">
        <div className="container">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="kicker">Co-Curricular Disciplines</div>
              <h2 className="h-section mt-2">Active Sports, Clubs & Performing Arts</h2>
              <div className="w-16 h-1 bg-primary mx-auto mt-4 rounded-full" />
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedActivities.map((activity, idx) => {
              const aImg = resolveImage(activity.image, '/images/page-banners/extra-curricullam.jpg');
              const desc = stripHtml(activity.description || activity.desc);
              return (
                <Reveal key={activity.id || idx} delayMs={idx * 50}>
                  <div className="card h-full flex flex-col overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 group hover:-translate-y-1 bg-white">
                    <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                      <img
                        src={aImg}
                        alt={activity.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/page-banners/extra-curricullam.jpg';
                        }}
                      />
                      {activity.category && (
                        <span className="absolute top-3 right-3 bg-black/60 backdrop-blur text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                          {activity.category}
                        </span>
                      )}
                      {activity.icon && (
                        <div className="absolute top-3 left-3 w-8 h-8 rounded-lg bg-white/95 backdrop-blur text-primary flex items-center justify-center shadow-md">
                          <i className={`fas ${activity.icon} text-xs`} />
                        </div>
                      )}
                    </div>

                    <div className="p-6 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="font-serif font-bold text-lg text-primary mb-2 group-hover:text-accent transition-colors">
                          {activity.title}
                        </h3>
                        <p className="text-muted text-xs leading-relaxed line-clamp-3">
                          {desc}
                        </p>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] font-semibold text-accent">
                        <span>Co-Curricular Activity</span>
                        <i className="fas fa-check-circle text-xs" />
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default ExtraCurriculumActivitiesPage;
